/**
 * ObjectivesTab Component
 * 
 * Employee objectives tab content - the most complex tab.
 * Handles KPA view, timeline/configuration view, and legacy year view.
 */

import { useState, useMemo } from 'react'
import { motion } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  LayoutGrid,
  ArrowLeft,
  Goal,
  Clock,
  Calendar,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Settings,
  User,
  Lightbulb,
  Plus
} from 'lucide-react'
import EmployeeKPAView from '@/components/performance/EmployeeKPAView'
import ObjectiveConfigurationDialog from '@/components/performance/manager/ObjectiveConfigurationDialog'
import { useJourneyViewMode } from '@/lib/journeyViewMode'
import { useOwnObjectiveHandlers } from '@/hooks/useOwnObjectiveHandlers'
import type { EmployeePerformancePlan, RoleKPI } from '@/services/api/performanceService'
import type { KPAData, EnhancedEvidenceEntry } from '@/types/evidence'
import type { HistoricalPerformanceResponse } from '@/services/api/integrationService'

// Props interfaces
interface EmployeeContext {
  jobGroup: string
  reportingManager: string
  reportingManagerId?: string
  campus: string
  department: string
  performanceCycle: string
}

interface UserInfo {
  id?: string
  employeeId?: string
  displayName?: string
  managerId?: string
  managerEmployeeId?: string
  jobTitle?: string
}

interface AISuggestion {
  id: string
  type: 'strategic' | 'operational'
  title: string
  description: string
  smartScore: number
  alignedPillar: string
  suggestedMetrics: string[]
}

interface ObjectivesTabProps {
  // User & context
  user: UserInfo | null
  employeeContext: EmployeeContext
  selectedYear: number
  currentYear: number
  selectedReviewPeriod: 'MIDYEAR' | 'YEAREND'
  
  // Data state
  isLoading: boolean
  error: string | null
  isLegacyYear: boolean
  hasObjectives: boolean
  performancePlan: EmployeePerformancePlan | null
  kpaData: KPAData[]
  roleKPIs: RoleKPI[]
  
  // Evidence
  evidence: EnhancedEvidenceEntry[]
  filteredEvidence: EnhancedEvidenceEntry[]
  onAddEvidence: (evidence: Omit<EnhancedEvidenceEntry, 'id' | 'createdBy' | 'updatedAt'>) => Promise<EnhancedEvidenceEntry | undefined>
  onUpdateEvidence: (evidenceId: string, updates: Partial<EnhancedEvidenceEntry>) => Promise<void>
  onDeleteEvidence: (evidenceId: string) => Promise<void>
  onUploadDocument: (file: File, evidenceData: Partial<EnhancedEvidenceEntry>) => Promise<void>
  onUpdateEvidenceClassification: (evidenceId: string, classification: EnhancedEvidenceEntry['classification']) => Promise<void>
  onRefreshEvidence: () => Promise<void>
  
  // Refetch functions
  refetchPerformancePlan: () => Promise<void>
  refetchCollaborativeSessions: () => Promise<void>
  
  // Legacy year data
  historicalData: HistoricalPerformanceResponse | null
  isLoadingHistorical: boolean
  
  // Profile handler
  onOpenProfileManager: () => void
  
  // View KPI details
  onViewKPIDetails: (kpi: RoleKPI) => void
  
  // Objective handlers (optional - uses internal stubs if not provided)
  onCreateObjective?: (objective: any) => Promise<void>
  onUpdateObjective?: (objectiveId: number, updates: any) => Promise<void>
  onDeleteObjective?: (objectiveId: number) => void
  onViewObjectiveDetails?: (objective: any) => void
}

export function ObjectivesTab({
  user,
  employeeContext,
  selectedYear,
  currentYear,
  selectedReviewPeriod,
  isLoading,
  error,
  isLegacyYear,
  hasObjectives,
  performancePlan,
  kpaData,
  roleKPIs,
  evidence,
  filteredEvidence,
  onAddEvidence,
  onUpdateEvidence,
  onDeleteEvidence,
  onUploadDocument,
  onUpdateEvidenceClassification,
  onRefreshEvidence,
  refetchPerformancePlan,
  refetchCollaborativeSessions,
  historicalData,
  isLoadingHistorical,
  onOpenProfileManager,
  onViewKPIDetails,
  onCreateObjective: onCreateObjectiveProp,
  onUpdateObjective: onUpdateObjectiveProp,
  onDeleteObjective: onDeleteObjectiveProp,
  onViewObjectiveDetails: onViewObjectiveDetailsProp
}: ObjectivesTabProps) {
  // Shared journey view mode (Simple ↔ Detailed). Drives RAG/% softening here
  // and the same toggle is rendered on the Portfolio tab. localStorage-backed
  // so the user's preference is consistent across surfaces.
  const [journeyViewMode] = useJourneyViewMode()

  // View mode state
  const [objectivesViewMode, setObjectivesViewMode] = useState<'kpa' | 'timeline'>('kpa')

  // The Team Member's own handlers for the objective dialog, for the year on screen, with the
  // meeting about the objectives that opens their approval. Shared with the year-end check-in's
  // step for next year's objectives (HR UAT list #27).
  const own = useOwnObjectiveHandlers({
    user,
    year: selectedYear,
    onChange: refetchPerformancePlan,
    onSessionChange: refetchCollaborativeSessions,
  })
  
  // AI suggestions (placeholder - could be loaded from API)
  const [showAISuggestions, setShowAISuggestions] = useState(false)
  const aiSuggestions: AISuggestion[] = useMemo(() => [
    {
      id: '1',
      type: 'strategic',
      title: 'Enhance Research Output Quality',
      description: 'Increase peer-reviewed publications with focus on AI/ML applications in education',
      smartScore: 85,
      alignedPillar: 'Impact',
      suggestedMetrics: ['Publications per year', 'Citation index', 'Research grant success rate']
    },
    {
      id: '2', 
      type: 'operational',
      title: 'Improve Student Engagement Rates',
      description: 'Implement innovative teaching methods to increase class attendance and participation',
      smartScore: 78,
      alignedPillar: 'Quality',
      suggestedMetrics: ['Class attendance %', 'Student evaluation scores', 'Assignment submission rates']
    }
  ], [])

  // Objective handlers - use props if provided, otherwise stub implementations
  const handleCreateObjective = onCreateObjectiveProp || ((objective: any) => {
    if (import.meta.env.DEV) console.log('Create objective (stub):', objective)
  })

  const handleUpdateObjective = onUpdateObjectiveProp || ((objectiveId: number, updates: any) => {
    if (import.meta.env.DEV) console.log('Update objective (stub):', objectiveId, updates)
  })

  const handleDeleteObjective = onDeleteObjectiveProp || ((objectiveId: number) => {
    if (import.meta.env.DEV) console.log('Delete objective (stub):', objectiveId)
  })

  const handleViewObjectiveDetails = onViewObjectiveDetailsProp || ((objective: any) => {
    if (import.meta.env.DEV) console.log('View objective details (stub):', objective)
  })

  // Legacy Year View - Historical F&O Data
  if (isLegacyYear) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 border border-amber-200"
      >
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 mb-4">
            <Clock className="h-8 w-8 text-amber-600" />
          </div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            Historical Impact & growth Data ({selectedYear})
          </h2>
          <p className="text-gray-600 max-w-xl mx-auto">
            This year's Impact & growth data is from the legacy F&O system. 
            The new Impact & growth platform launched in 2026
          </p>
        </div>

        {isLoadingHistorical ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600 mr-3"></div>
            <span className="text-amber-700">Loading historical data from F&O...</span>
          </div>
        ) : historicalData?.reviews && historicalData.reviews.length > 0 ? (
          <div className="space-y-4">
            {historicalData.reviews.map((review, index) => (
              <Card key={index} className="bg-white/80">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <Calendar className="h-5 w-5 text-amber-600" />
                      <span className="font-semibold">{review.review_type || '1:1 Check-In Dialogue'}</span>
                    </div>
                    <Badge variant={review.final_rating_description === 'Exceptional' ? 'default' : 'secondary'}>
                      {review.final_rating_description || 'Pending'}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Year</span>
                      <p className="font-medium">{review.year || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Period</span>
                      <p className="font-medium">{review.period_id || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Status</span>
                      <div className="flex items-center gap-1">
                        {review.approval_status === 'Approved' || review.approval_status === 'Completed' ? (
                          <>
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            <span className="text-green-700">Completed</span>
                          </>
                        ) : (
                          <>
                            <XCircle className="h-4 w-4 text-gray-400" />
                            <span className="text-gray-500">{review.approval_status || 'Incomplete'}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-500">Impact &amp; Growth Indicator</span>
                      <p className="font-medium">{review.final_rating_description || review.manager_rating_description || 'N/A'}</p>
                    </div>
                  </div>
                  {review.comments && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <p className="text-sm text-gray-600">{review.comments}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <AlertTriangle className="h-12 w-12 text-amber-400 mx-auto mb-3" />
            <p className="text-gray-600">No historical impact & growth data found for {selectedYear}.</p>
          </div>
        )}
      </motion.div>
    )
  }

  // No Objectives State - Onboarding (but allow timeline view if user wants to configure)
  if (!hasObjectives && !isLoading && objectivesViewMode !== 'timeline') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-heritage-blue/5 to-teal-50 rounded-2xl p-8 border border-heritage-blue/20"
        data-tour="empty-state"
      >
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-heritage-blue/10 mb-6">
            <Goal className="h-10 w-10 text-heritage-blue" />
          </div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">
            {/* OLD: Waiting for Objectives */}
            Setting Objectives
          </h2>
          {/* OLD: <p className="text-gray-600 mb-6 max-w-lg mx-auto">
            Your performance objectives haven't been set up yet. Here's what happens next:
          </p> */}
          <div className="bg-white/70 rounded-xl p-6 mb-6 max-w-2xl mx-auto text-left">
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start gap-3">
                <div className="p-1 bg-heritage-blue/10 rounded-full mt-1">
                  <div className="w-2 h-2 bg-heritage-blue rounded-full" />
                </div>
                <span><strong>Your people leader will schedule</strong> a collaborative objective-setting session with you</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="p-1 bg-heritage-blue/10 rounded-full mt-1">
                  <div className="w-2 h-2 bg-heritage-blue rounded-full" />
                </div>
                <span><strong>Together you'll create</strong> meaningful objectives aligned with your role and the organisation's strategic pillars</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="p-1 bg-heritage-blue/10 rounded-full mt-1">
                  <div className="w-2 h-2 bg-heritage-blue rounded-full" />
                </div>
                <span><strong>Your KPIs will be assigned</strong> to help you track progress throughout the year</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="p-1 bg-heritage-blue/10 rounded-full mt-1">
                  <div className="w-2 h-2 bg-heritage-blue rounded-full" />
                </div>
                <span><strong>You'll receive reminders</strong> when your impact & growth plan is ready</span>
              </li>
            </ul>
          </div>
          {/* OLD: Already displayed in blue banner
          <p className="text-sm text-gray-600 mb-6">
            Your people leader: <strong>{employeeContext.reportingManager}</strong><br />
            Function: <strong>{employeeContext.department}</strong> | Campus: <strong>{employeeContext.campus}</strong>
          </p> */}
          <div className="flex items-center justify-center gap-4">
            {/* OLD: <Button variant="outline" className="gap-2" onClick={onOpenProfileManager}>
              <User className="h-4 w-4" />
              Update My Profile
            </Button> */}
            <Button
              className="gap-2 bg-heritage-blue hover:bg-heritage-blue/90"
              onClick={() => setObjectivesViewMode('timeline')}
              data-tour="configure-objectives"
            >
              {/* OLD: <Settings /> Configure Objectives */}
              Objectives
            </Button>
          </div>
        </div>
      </motion.div>
    )
  }

  // Timeline/Configuration View
  if (objectivesViewMode === 'timeline') {
    const pending = (performancePlan?.objectives || []).filter(obj => obj.status === 'pending_approval')
    const active = (performancePlan?.objectives || []).filter(obj => obj.status === 'active')
    const rejected = (performancePlan?.objectives || []).filter(obj => obj.status === 'rejected')

    return (
      <div className="mt-6">
        {/* Back to Dashboard Navigation */}
        <Card 
          className="mb-6 border-heritage-blue/20 bg-gradient-to-r from-heritage-blue/5 to-transparent hover:border-heritage-blue/40 transition-all cursor-pointer group" 
          onClick={() => setObjectivesViewMode('kpa')} 
          data-tour="return-dashboard"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-heritage-blue/10 flex items-center justify-center group-hover:bg-heritage-blue/20 transition-colors">
                  <LayoutGrid className="h-5 w-5 text-heritage-blue" />
                </div>
                <div>
                  {/* OLD: Return to Performance Dashboard / View KPAs overview and performance summary */}
                  <h3 className="font-semibold text-heritage-blue">Return to Objectives Dashboard</h3>
                  <p className="text-sm text-gray-500">View KPA overview and your objectives summary</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" className="gap-2 text-heritage-blue hover:text-heritage-blue hover:bg-heritage-blue/10">
                <ArrowLeft className="h-4 w-4" />
                Go Back
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Objective Configuration Dialog (Inline) */}
        <ObjectiveConfigurationDialog
          open={true}
          onOpenChange={() => {}}
          inline={true}
          userRole="employee"
          selectedYear={selectedYear}
          teamMember={{
            employee_id: user?.employeeId || user?.id || '',
            display_name: user?.displayName || 'Current User',
            job_title: employeeContext.jobGroup,
            campus: employeeContext.campus,
            department: employeeContext.department,
            objectives: {
              total: performancePlan?.total_objectives || 0,
              onTrack: 0,
              atRisk: 0,
              offTrack: 0,
              completed: 0,
              pending_approval: pending.length,
              progress: performancePlan?.overall_progress || 0
            },
            pending_objectives: pending as any,
            active_objectives: active as any,
            rejected_objectives: rejected as any
          }}
          onApprove={own.handleApprove}
          onReject={own.handleReject}
          onUpdate={own.handleUpdate}
          onAllocate={own.handleAllocate}
          onObjectivesChange={async () => {
            await refetchPerformancePlan()
          }}
          onObjectivesAdded={own.handleObjectivesAdded}
        />
      </div>
    )
  }

  // KPA View - Main Dashboard View
  return (
    <div className="min-w-0 max-w-full" data-tour="kpa-view">
      {/* Historical View Banner */}
      {selectedYear < currentYear && (
        <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border-l-4 border-yellow-400 p-4 rounded-lg mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Viewing Historical Data ({selectedYear})</h4>
              <p className="text-sm text-gray-600">
                You are viewing archived objectives and KPIs from {selectedYear}. This data is read-only.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* AI Suggestions Panel — gated to Detailed view. In Simple view the
          calm editorial treatment intentionally omits AI scoring/templates so
          the page stays low-pressure. */}
      {journeyViewMode === 'detailed' && showAISuggestions && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="bg-stone-50 rounded-xl p-6 border border-stone-200 mb-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <Lightbulb className="h-5 w-5 text-stone-500" />
            <h3 className="font-serif text-lg text-stone-800">Ideas to start from</h3>
            <Badge className="bg-stone-100 text-stone-600 font-normal">Shaped around your role &amp; journey</Badge>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {aiSuggestions.map((suggestion) => (
              <div key={suggestion.id} className="bg-white rounded-lg p-4 border border-stone-200 shadow-sm">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Badge variant={suggestion.type === 'strategic' ? 'default' : 'secondary'}>
                      {suggestion.type}
                    </Badge>
                    {journeyViewMode === 'detailed' && (
                      <span className="text-sm text-stone-400">SMART Score: {suggestion.smartScore}%</span>
                    )}
                  </div>
                  <Button size="sm" className="text-xs">
                    <Plus className="h-3 w-3 mr-1" />
                    Use Template
                  </Button>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">{suggestion.title}</h4>
                <p className="text-sm text-gray-600 mb-3">{suggestion.description}</p>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span>Aligns with: {suggestion.alignedPillar}</span>
                  <span>•</span>
                  <span>{suggestion.suggestedMetrics.length} metrics suggested</span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* KPA View — the Simple/Detailed "Show details" toggle now lives inline
          in EmployeeKPAView's nav controls cluster (no dedicated row). */}
      {!isLoading && !error && kpaData.length > 0 && (
        <EmployeeKPAView
          kpas={kpaData}
          employeeId={user?.employeeId || ""}
          employeeName={user?.displayName || "Current User"}
          employeeRole={employeeContext.jobGroup}
          reviewPeriod={selectedReviewPeriod}
          journeyViewMode={journeyViewMode}

          // Evidence - filtered by selected review period
          evidence={filteredEvidence}
          onAddEvidence={onAddEvidence}
          onUpdateEvidence={onUpdateEvidence}
          onUpdateEvidenceClassification={onUpdateEvidenceClassification}
          onRefreshEvidence={onRefreshEvidence}
          onDeleteEvidence={onDeleteEvidence}
          onUploadDocument={onUploadDocument}

          // Objectives
          onCreateObjective={handleCreateObjective}
          onUpdateObjective={handleUpdateObjective}
          onDeleteObjective={handleDeleteObjective}
          onViewObjectiveDetails={handleViewObjectiveDetails}
          onConfigureObjectives={() => setObjectivesViewMode('timeline')}

          // KPIs
          onViewKPIDetails={onViewKPIDetails}
        />
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-heritage-blue mr-3"></div>
          <span className="text-heritage-blue font-secondary">Loading objectives...</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50/90 backdrop-blur-sm rounded-2xl border border-red-200 p-6 text-center">
          <div className="text-red-600 font-secondary">
            <span>Error: {error}</span>
          </div>
        </div>
      )}

      {/* No KPA Data */}
      {!isLoading && !error && kpaData.length === 0 && hasObjectives && (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <AlertTriangle className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No KPA data available. Please contact your people leader.</p>
        </div>
      )}
    </div>
  )
}

export default ObjectivesTab
